import { supabase } from "../../services/SupabaseService";
import { getCurrentUserId } from "../../utils/helpers";
import { Term } from "../../entities/Term";
import { fetchAllRows } from "../../utils/batching";

class TermsRepository {
  private getUserId(): string {
    const id = getCurrentUserId();
    if (!id) throw new Error("User not authenticated");
    return id;
  }

  /** Loads all active terms of a project, in pages (a single request returns at most 1000 rows). */
  async getByProject(projectId: string): Promise<Term[]> {
    const userId = this.getUserId();
    const rows = await fetchAllRows((from, to) =>
      supabase
        .from("terms")
        .select("*", { count: "exact" })
        .eq("project_id", projectId)
        .eq("user_id", userId)
        .is("deleted_at", null)
        .order("key", { ascending: true })
        .order("id", { ascending: true })
        .range(from, to)
    );
    return rows.map(Term.fromRow);
  }

  async getByProjectPaginated(
    projectId: string,
    page: number,
    pageSize: number,
    search?: string
  ): Promise<{ terms: Term[]; total: number }> {
    const userId = this.getUserId();
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from("terms")
      .select("*", { count: "exact" })
      .eq("project_id", projectId)
      .eq("user_id", userId)
      .is("deleted_at", null)
      .order("key", { ascending: true })
      .range(from, to);

    if (search?.trim()) {
      query = query.ilike("key", `%${search.trim()}%`);
    }

    const { data, error, count } = await query;
    if (error) throw new Error(error.message);
    return { terms: (data || []).map(Term.fromRow), total: count ?? 0 };
  }

  /** Search terms by key (partial, case-insensitive) within a project. */
  async searchByKey(projectId: string, query: string): Promise<Term[]> {
    const userId = this.getUserId();
    const { data, error } = await supabase
      .from("terms")
      .select("*")
      .eq("project_id", projectId)
      .eq("user_id", userId)
      .is("deleted_at", null)
      .ilike("key", `%${query}%`)
      .order("key", { ascending: true });

    if (error) throw new Error(error.message);
    return (data || []).map(Term.fromRow);
  }

  async create(projectId: string, key: string, isPlural: boolean, context: string | null): Promise<Term> {
    const userId = this.getUserId();
    const { data, error } = await supabase
      .from("terms")
      .insert({
        project_id: projectId,
        key: key.trim(),
        is_plural: isPlural,
        context: context?.trim() || null,
        user_id: userId,
      })
      .select()
      .single();

    if (error) {
      if (error.code === "23505") {
        throw new Error("A term with this key already exists in the project.");
      }
      throw new Error(error.message);
    }
    if (!data) throw new Error("Term could not be created.");
    return Term.fromRow(data);
  }

  /** Creates several terms in a single request. Either all of them are created, or none. */
  async createMany(projectId: string, terms: { key: string; isPlural: boolean }[]): Promise<Term[]> {
    if (terms.length === 0) return [];
    const userId = this.getUserId();
    const { data, error } = await supabase
      .from("terms")
      .insert(terms.map((t) => ({ project_id: projectId, key: t.key.trim(), is_plural: t.isPlural, context: null, user_id: userId })))
      .select();

    if (error) {
      if (error.code === "23505") {
        throw new Error("One of these keys already exists in the project.");
      }
      throw new Error(error.message);
    }
    return (data || []).map(Term.fromRow);
  }

  async update(id: string, key: string, isPlural: boolean, context: string | null): Promise<Term> {
    const userId = this.getUserId();
    const { data, error } = await supabase
      .from("terms")
      .update({ key: key.trim(), is_plural: isPlural, context: context?.trim() || null })
      .eq("id", id)
      .eq("user_id", userId)
      .select()
      .single();

    if (error) {
      if (error.code === "23505") {
        throw new Error("A term with this key already exists in the project.");
      }
      throw new Error(error.message);
    }
    if (!data) throw new Error("Term not found.");
    return Term.fromRow(data);
  }

  async softDeleteMany(ids: string[]): Promise<void> {
    const userId = this.getUserId();
    const { error } = await supabase.from("terms").update({ deleted_at: new Date().toISOString() }).in("id", ids).eq("user_id", userId);

    if (error) throw new Error(error.message);
  }
}

export default TermsRepository;
